import { type Inspection } from '@lib/spproject/types';
import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type IFolder } from './folderEndpoints';

export interface IProject {
  projectInfo: ProjectInfo;
  inspections: Inspection.Info[];
  parentFolder?: PartialProjectFolderInfo | string | null;
}

export interface ProjectInfo {
  version: number;
  id: string;
  name: string;
  isProtected?: boolean;
  dateCreated: string;
  dateModified: string;
}

export type PartialProjectFolderInfo = Pick<IFolder, '_id' | 'name' | 'path'> | null;

export const projectApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<IProject[], { search?: string; }>({
      query: ({ search }) => ({
        url: '/v1/projects',
        params: search ? { search } : undefined,
      }),
      keepUnusedDataFor: 60,
      providesTags: ['Project'],
      onQueryStarted: handleApiResult(),
    }),
    getProject: builder.query<IProject, string>({
      query: (id) => `/v1/projects/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }],
      onQueryStarted: handleApiResult(),
    }),
    getProjectFile: builder.query<string, string>({
      query: (id) => ({
        url: `/v1/projects/${id}/file`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
      onQueryStarted: handleApiResult(),
    }),
    getProjectThumbnail: builder.query<string, string>({
      query: (id) => ({
        url: `/v1/projects/${id}/thumbnail`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
    }),
    addProject: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: '/v1/projects',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: handleApiResult('Api_Project_Add_Successful'),
    }),
    updateProject: builder.mutation<void, { id: string, formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `/v1/projects/${id}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: handleApiResult('Api_Project_Update_Successful'),
    }),
    patchProject: builder.mutation<void, { id: string } & Omit<IProject, 'projectInfo' | 'inspections'>>({
      query: ({ id, ...rest }) => ({
        url: `/v1/projects/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: handleApiResult('Api_Project_Update_Successful'),
    }),
    deleteProject: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project', 'Folder'],
      onQueryStarted: handleApiResult('Api_Project_Delete_Successful'),
    }),
  }),
});

export const thumbnailCache = new Map<string, string | null>();

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useLazyGetProjectQuery,
  useLazyGetProjectFileQuery,
  useLazyGetProjectThumbnailQuery,
  useAddProjectMutation,
  useUpdateProjectMutation,
  usePatchProjectMutation,
  useDeleteProjectMutation,
} = projectApiSlice;
